import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  ForecastAdjustment, ForecastAdjustmentRequest, ForecastConfidence, ForecastItem, ForecastResponse, ForecastSettings,
  ForecastSettingsRequest,
} from '../interfaces/cashForecast.interface';
import { saveBlob } from '../utils/saveBlob';
import { parseContentDispositionFilename } from '../utils/contentDisposition';

interface GridParams { from?: string; weeks?: number; confidence?: ForecastConfidence[] }

const toParams = (p: GridParams) => ({
  from: p.from || undefined,
  weeks: p.weeks || undefined,
  confidence: p.confidence && p.confidence.length ? p.confidence.join(',') : undefined,
});

export const CashForecastService = {
  grid: async (institutionId: string, p: GridParams = {}): Promise<ForecastResponse> =>
    (await axios.get(ENDPOINTS.CASH_FORECAST.GRID(institutionId), { params: toParams(p) })).data,

  /** Items behind a cell (1-based week) or a whole row (week omitted). */
  items: async (institutionId: string, p: GridParams, rowKey: string, week?: number): Promise<ForecastItem[]> =>
    (await axios.get(ENDPOINTS.CASH_FORECAST.ITEMS(institutionId), { params: { ...toParams(p), rowKey, week } })).data,

  exportXlsx: async (institutionId: string, p: GridParams = {}): Promise<void> => {
    const response = await axios.get(ENDPOINTS.CASH_FORECAST.XLSX(institutionId), { params: toParams(p), responseType: 'blob' });
    const disposition: string = response.headers?.['content-disposition'] ?? '';
    saveBlob(new Blob([response.data]), parseContentDispositionFilename(disposition, 'cash-forecast.xlsx'));
  },

  getSettings: async (institutionId: string): Promise<ForecastSettings> =>
    (await axios.get(ENDPOINTS.CASH_FORECAST.SETTINGS(institutionId))).data,
  saveSettings: async (institutionId: string, req: ForecastSettingsRequest): Promise<ForecastSettings> =>
    (await axios.put(ENDPOINTS.CASH_FORECAST.SETTINGS(institutionId), req)).data,

  listAdjustments: async (institutionId: string): Promise<ForecastAdjustment[]> =>
    (await axios.get(ENDPOINTS.CASH_FORECAST.ADJUSTMENTS(institutionId))).data,
  createAdjustment: async (institutionId: string, req: ForecastAdjustmentRequest): Promise<ForecastAdjustment> =>
    (await axios.post(ENDPOINTS.CASH_FORECAST.ADJUSTMENTS(institutionId), req)).data,
  updateAdjustment: async (institutionId: string, id: string, req: ForecastAdjustmentRequest): Promise<ForecastAdjustment> =>
    (await axios.put(ENDPOINTS.CASH_FORECAST.ADJUSTMENT(institutionId, id), req)).data,
  deleteAdjustment: async (institutionId: string, id: string): Promise<void> => {
    await axios.delete(ENDPOINTS.CASH_FORECAST.ADJUSTMENT(institutionId, id));
  },
};
